import React, { useState } from 'react';
import type { UngroupedTicket } from '../../lib/types';
import { formatPrice } from '../../lib/format';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';

function formatScrapedAt(scraped_at?: string | null): string | null {
  if (!scraped_at) return null;
  const d = new Date(scraped_at);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Effective "as of" date: prefer last_seen_at, fall back to scraped_at */
function effectiveDate(ticket: UngroupedTicket): string | null {
  return formatScrapedAt(ticket.last_seen_at ?? ticket.scraped_at);
}

interface AllTicketsTableProps {
  tickets: UngroupedTicket[];
}

export default function AllTicketsTable({ tickets }: AllTicketsTableProps) {
  const [expanded, setExpanded] = useState(false);

  if (tickets.length === 0) return null;

  const previewCount = 6;
  const allFlat = [...tickets].sort((a, b) => {
    if (a.price === null && b.price === null) return 0;
    if (a.price === null) return 1;
    if (b.price === null) return -1;
    return a.price - b.price;
  });
  const displayTickets = expanded ? allFlat : allFlat.slice(0, previewCount);
  const hasMore = allFlat.length > previewCount;

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3 border-b border-slate-700/50 bg-slate-800/80">
        <h3 className="text-sm font-semibold text-slate-200">
          Other Available Tickets
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">
          {tickets.length} ticket{tickets.length !== 1 ? 's' : ''} not matched to a comparison group
        </p>
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-700/50">
              <th className="text-left px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                Ticket
              </th>
              <th className="text-left px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                Supplier
              </th>
              <th className="text-right px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                Price
              </th>
              <th className="text-right px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                Price obtained
              </th>
              <th className="text-right px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider w-16">
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/30">
            {displayTickets.map((ticket) => {
              const available = ticket.is_available !== false;

              return (
                <tr
                  key={ticket.ticket_id}
                  className={`hover:bg-slate-700/20 transition-colors ${!available ? 'opacity-60' : ''}`}
                >
                  <td className="px-5 py-3 max-w-xs">
                    <span className={`truncate block ${available ? 'text-slate-300' : 'text-slate-500'}`}>
                      {ticket.title}
                    </span>
                    {!available && (
                      <span className="inline-block mt-1 text-[10px] font-medium text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded px-1.5 py-0.5">
                        Unavailable
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-slate-400">
                    {ticket.supplier.name}
                  </td>
                  <td className={`px-5 py-3 text-right font-medium ${available ? 'text-slate-200' : 'text-slate-500'}`}>
                    {formatPrice(ticket.price)}
                  </td>
                  <td className="px-5 py-3 text-right text-xs text-slate-500">
                    {!available && formatScrapedAt(ticket.unavailable_since) ? (
                      <span className="text-rose-400/70">Unavailable since {formatScrapedAt(ticket.unavailable_since)}</span>
                    ) : (
                      effectiveDate(ticket) ?? '--'
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {available && (
                      <a
                        href={ticket.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-slate-500 hover:text-emerald-400 transition-colors"
                      >
                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                      </a>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="sm:hidden divide-y divide-slate-700/30">
        {displayTickets.map((ticket) => {
          const available = ticket.is_available !== false;

          return (
            <div key={ticket.ticket_id} className={`px-4 py-3 ${!available ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className={`text-sm truncate ${available ? 'text-slate-300' : 'text-slate-500'}`}>{ticket.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{ticket.supplier.name}</p>
                  {!available && (
                    <p className="text-[10px] text-rose-400/70 mt-0.5">No longer available</p>
                  )}
                  {effectiveDate(ticket) && (
                    <p className="text-[10px] text-slate-600 mt-0.5">as of {effectiveDate(ticket)}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-sm font-medium ${available ? 'text-slate-200' : 'text-slate-500'}`}>
                    {formatPrice(ticket.price)}
                  </span>
                  {available && (
                    <a
                      href={ticket.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-500 hover:text-emerald-400 transition-colors"
                    >
                      <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Expand/collapse */}
      {hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-center gap-1.5 py-3
                     text-xs font-medium text-slate-400 hover:text-slate-200
                     border-t border-slate-700/50 hover:bg-slate-700/20 transition-colors"
        >
          {expanded ? (
            <>Show less <ChevronUpIcon className="w-3.5 h-3.5" /></>
          ) : (
            <>Show all {allFlat.length} tickets <ChevronDownIcon className="w-3.5 h-3.5" /></>
          )}
        </button>
      )}
    </div>
  );
}
